/**
 * Skylite Language Context
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 */

import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useRef } from 'react';
import {
  SUPPORTED_LANGUAGES,
  getTranslation,
  appLanguageToGoogleCode,
  GOOGLE_TRANSLATE_INCLUDED_LANGUAGES
} from '../config/languages';

const LanguageContext = createContext();

const GOOGLE_TO_APP = {
  en: 'en',
  ar: 'ar',
  'zh-CN': 'zh',
  hi: 'hi',
  es: 'es',
  pt: 'pt',
  id: 'id',
  fr: 'fr'
};

function clearGoogTransCookies() {
  try {
    const past = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const hostname = window.location.hostname || '';
    const parts = hostname.split('.').filter(Boolean);
    const parentDomain = parts.length > 2 ? parts.slice(-2).join('.') : '';
    const domains = [hostname, hostname ? `.${hostname}` : '', parentDomain, parentDomain ? `.${parentDomain}` : ''].filter(Boolean);
    const path = window.location.pathname || '/';
    const paths = ['/', path, path.replace(/\/[^/]*$/, '/') || '/'];
    domains.forEach((domain) => {
      paths.forEach((cookiePath) => {
        document.cookie = `googtrans=;expires=${past};max-age=0;domain=${domain};path=${cookiePath}`;
      });
    });
    paths.forEach((cookiePath) => {
      document.cookie = `googtrans=;expires=${past};max-age=0;path=${cookiePath}`;
    });
  } catch (_) {
    /* ignore */
  }
}

function syncSkyliteLanguageStorage(appCode) {
  const googleCode = appLanguageToGoogleCode(appCode);
  try {
    localStorage.setItem('skylite-language', appCode);
    localStorage.setItem('skylite-google-translate', googleCode);
    localStorage.setItem('commandline_selected_language', googleCode);
  } catch (_) {
    /* ignore */
  }
  if (document.documentElement) {
    document.documentElement.lang = appCode === 'en' ? 'en' : googleCode;
    document.documentElement.dir = appCode === 'ar' ? 'rtl' : 'ltr';
  }
}

function clearEnglishTranslationState() {
  try {
    if (typeof window.clearAllGoogTransCookies === 'function') {
      window.clearAllGoogTransCookies();
    } else {
      clearGoogTransCookies();
    }
    localStorage.removeItem('googtrans');
    syncSkyliteLanguageStorage('en');
  } catch (_) {
    /* ignore */
  }
}

/**
 * Default to English chrome on first paint. Stale googtrans cookies are cleared so Arabic
 * does not flash before React/auth settles. Stored preference is kept in state and applied
 * only after App confirms this is not owner/impersonation English chrome.
 */
function resolveInitialSkyliteLanguage() {
  try {
    if (typeof window.clearAllGoogTransCookies === 'function') {
      window.clearAllGoogTransCookies();
    } else {
      clearGoogTransCookies();
    }

    const readPreferred = () => {
      const appStored = localStorage.getItem('skylite-language');
      if (appStored && SUPPORTED_LANGUAGES[appStored]) return appStored;
      const googleStored = localStorage.getItem('skylite-google-translate');
      if (googleStored && GOOGLE_TO_APP[googleStored]) return GOOGLE_TO_APP[googleStored];
      const commandlineStored = localStorage.getItem('commandline_selected_language');
      if (commandlineStored && GOOGLE_TO_APP[commandlineStored]) return GOOGLE_TO_APP[commandlineStored];
      return null;
    };

    const preferred = readPreferred();

    // Always LTR English until App chrome rules run — prevents GT start→reject on impersonation.
    try {
      document.documentElement.lang = 'en';
      document.documentElement.dir = 'ltr';
      document.body?.setAttribute('dir', 'ltr');
      document.body?.setAttribute('lang', 'en');
      window.__skyliteForceEnglishChrome = true;
    } catch (_) {
      /* ignore */
    }

    return preferred || 'en';
  } catch (_) {
    /* ignore */
  }

  clearEnglishTranslationState();
  return 'en';
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState(() => resolveInitialSkyliteLanguage());
  // Start forced-English; App turns this off only when chrome may use the stored language.
  const [forceEnglishDisplay, setForceEnglishDisplay] = useState(true);
  const [isRTL, setIsRTL] = useState(false);
  const translateMgrRef = useRef(null);
  const currentLanguageRef = useRef(currentLanguage);
  const forceEnglishDisplayRef = useRef(forceEnglishDisplay);
  const skipFirstGtLangEffect = useRef(true);
  currentLanguageRef.current = currentLanguage;
  forceEnglishDisplayRef.current = forceEnglishDisplay;

  // UI language for t()/RTL — may be forced to English on owner/teacher chrome without wiping prefs
  const uiLanguage = forceEnglishDisplay ? 'en' : currentLanguage;

  /**
   * Before paint: keep Google storage/cookies and document direction aligned with selection.
   * When forceEnglishDisplay is on, keep LTR English chrome but do not rewrite stored language prefs.
   */
  useLayoutEffect(() => {
    if (forceEnglishDisplay || currentLanguage === 'en') {
      try {
        if (typeof window.clearAllGoogTransCookies === 'function') {
          window.clearAllGoogTransCookies();
        } else {
          clearGoogTransCookies();
        }
        localStorage.removeItem('googtrans');
        if (document.documentElement) {
          document.documentElement.lang = 'en';
          document.documentElement.dir = 'ltr';
        }
        document.body?.setAttribute('dir', 'ltr');
        document.body?.setAttribute('lang', 'en');
      } catch (_) {
        /* ignore */
      }
      // Only wipe twin keys when user actually chose English — not when chrome forces display
      if (!forceEnglishDisplay && currentLanguage === 'en') {
        clearEnglishTranslationState();
      }
    } else {
      try {
        syncSkyliteLanguageStorage(currentLanguage);
        const g = appLanguageToGoogleCode(currentLanguage);
        const cookieVal = `/en/${g}`;
        document.cookie = `googtrans=${cookieVal};path=/`;
        document.cookie = `googtrans=${cookieVal};domain=${window.location.hostname};path=/`;
      } catch (_) {
        /* ignore */
      }
    }
    setIsRTL(uiLanguage === 'ar');
  }, [currentLanguage, forceEnglishDisplay, uiLanguage]);

  // Google Translate (auto-translation.js) rewrites visible text so new screens get translation
  // without adding every phrase to languages.js. React t() still supplies structured UI strings.
  // Language chrome (LanguageSelector) stays notranslate so the picker does not break.

  useEffect(() => {
    try {
      localStorage.setItem('skylite-language', currentLanguage);
    } catch (_) {
      /* ignore */
    }

    if (window.terminalWebSocket && window.terminalWebSocket.readyState === WebSocket.OPEN) {
      window.terminalWebSocket.send(
        JSON.stringify({
          type: 'language',
          language: currentLanguage
        })
      );
    }
  }, [currentLanguage]);

  useEffect(() => {
    if (typeof window.createAutoTranslationManager !== 'function') {
      console.warn('[Skylite] auto-translation.js missing; only t() translations apply.');
      return undefined;
    }

    const mgr = window.createAutoTranslationManager({
      storageKey: 'skylite-google-translate',
      defaultLanguage: 'en',
      includedLanguages: GOOGLE_TRANSLATE_INCLUDED_LANGUAGES,
      syncStorageKeys: ['commandline_selected_language'],
      observeMutations: true,
      scriptId: 'skylite-google-translate-script',
      translateElementId: 'skylite_google_translate_element',
      scriptCallbackName: 'skyliteGoogleTranslateElementInit'
    });

    translateMgrRef.current = mgr;
    window.__skyliteAutoTranslateManager = mgr;

    if (forceEnglishDisplayRef.current || window.__skyliteForceEnglishChrome) {
      try {
        mgr.suspendTranslation();
      } catch (_) {
        /* ignore */
      }
    }

    mgr.onReady(() => {
      if (forceEnglishDisplayRef.current || window.__skyliteForceEnglishChrome || (mgr.isSuspended && mgr.isSuspended())) {
        try {
          mgr.suspendTranslation();
        } catch (_) {
          mgr.applyLanguage('en', { persist: false, reloadIfUnavailable: false, force: true });
        }
        return;
      }
      const app = currentLanguageRef.current;
      const g = appLanguageToGoogleCode(app);
      // reloadIfUnavailable:true caused full reloads and a visible English→Arabic flash on hard refresh.
      mgr.applyLanguage(g, { persist: true, reloadIfUnavailable: false });
    });

    mgr.init();

    if (forceEnglishDisplayRef.current || window.__skyliteForceEnglishChrome) {
      try {
        mgr.suspendTranslation();
      } catch (_) {
        /* ignore */
      }
    }

    return () => {
      try {
        mgr.cleanup();
      } catch (_) {
        /* ignore */
      }
      translateMgrRef.current = null;
      if (window.__skyliteAutoTranslateManager === mgr) {
        delete window.__skyliteAutoTranslateManager;
      }
    };
  }, []);

  useEffect(() => {
    const mgr = translateMgrRef.current;
    if (!mgr || typeof mgr.applyLanguage !== 'function') return;
    if (skipFirstGtLangEffect.current) {
      skipFirstGtLangEffect.current = false;
      return;
    }
    if (forceEnglishDisplay) {
      try {
        if (typeof mgr.suspendTranslation === 'function') mgr.suspendTranslation();
        else mgr.applyLanguage('en', { persist: false, force: true });
      } catch (_) {
        /* ignore */
      }
      return;
    }
    const g = appLanguageToGoogleCode(currentLanguage);
    window.requestAnimationFrame(() => {
      try {
        mgr.applyLanguage(g, { persist: true, reloadIfUnavailable: false });
      } catch (_) {
        /* ignore */
      }
    });
  }, [currentLanguage, forceEnglishDisplay]);

  const changeLanguage = (languageCode) => {
    if (SUPPORTED_LANGUAGES[languageCode]) {
      setCurrentLanguage(languageCode);
    }
  };

  const t = (key) => {
    return getTranslation(key, uiLanguage);
  };

  const value = {
    currentLanguage,
    changeLanguage,
    t,
    isRTL,
    forceEnglishDisplay,
    setForceEnglishDisplay,
    supportedLanguages: SUPPORTED_LANGUAGES
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};
